import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BoardField, ExportFormat } from '../../board-v3.types';
import { BoardService } from '../../../../services/board.service';
import { NotificationService } from '../../../../services/notifications.service';

@Component({
  selector: 'app-board-v3-export-fields',
  templateUrl: './export-fields.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ExportFieldsComponent implements OnChanges {
  @Input() funnelId: string | null = null;
  @Input() fields: BoardField[] = [];

  @Output() close = new EventEmitter<void>();

  private readonly boardService = inject(BoardService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly formats: ExportFormat[] = ['CSV', 'JSON', 'TOML'];
  format: ExportFormat = 'CSV';
  selectedKeys = new Set<string>();
  isExporting = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields']) {
      this.selectedKeys = new Set((this.fields ?? []).map((field) => this.fieldKey(field)));
    }
  }

  get standardFields(): BoardField[] {
    return (this.fields ?? []).filter((field) => field.is_system);
  }

  get customFields(): BoardField[] {
    return (this.fields ?? []).filter((field) => !field.is_system);
  }

  get selectedCount(): number {
    return this.selectedKeys.size;
  }

  fieldKey(field: BoardField): string {
    return String(field.identifier ?? field.id ?? '');
  }

  isSelected(field: BoardField): boolean {
    return this.selectedKeys.has(this.fieldKey(field));
  }

  toggleField(field: BoardField, value: boolean): void {
    const key = this.fieldKey(field);
    if (value) this.selectedKeys.add(key);
    else this.selectedKeys.delete(key);
  }

  toggleAll(value: boolean): void {
    this.selectedKeys = value ? new Set((this.fields ?? []).map((field) => this.fieldKey(field))) : new Set();
  }

  onCancel(): void {
    if (this.isExporting) return;
    this.close.emit();
  }

  onExport(): void {
    if (!this.funnelId || !this.selectedCount || this.isExporting) return;

    const fields = (this.fields ?? [])
      .filter((field) => this.selectedKeys.has(this.fieldKey(field)))
      .map((field) => ({ key: this.fieldKey(field), is_custom: !field.is_system }));

    this.isExporting = true;
    this.boardService
      .exportCards(this.funnelId, {
        format: this.format,
        fields,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isExporting = false)),
      )
      .subscribe({
        next: () => {
          this.close.emit();
        },
        error: (error: any) => {
          this.notificationService.showError(
            '',
            error?.error?.message || this.translateService.instant('board_v3_export_failed'),
          );
        },
      });
  }
}
